// Shared content of the appearance controls (dark/light, header color,
// font size, fixed header) — rendered inside a floating popover on
// desktop (SettingsFab.jsx) and inside a Dialog from the profile menu
// on mobile (AdminHeader.jsx), so there's exactly one implementation of
// the controls themselves.
import React from "react";
import { Box, Typography, Tooltip, Divider, Switch } from "@mui/material";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import { useThemeContext } from "../../contexts/ThemeContext";

const AppearancePanel = () => {
  const {
    mode, toggleMode,
    headerColor, changeHeaderColor, HEADER_COLORS,
    headerFixed, toggleHeaderFixed,
    fontSize, changeFontSize, FONT_SIZES,
  } = useThemeContext();

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {mode === "dark" ? <DarkModeIcon sx={{ fontSize: 16, color: "text.secondary" }} /> : <LightModeIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
            {mode === "dark" ? "Dark" : "Light"} Mode
          </Typography>
        </Box>
        <Switch size="small" checked={mode === "dark"} onChange={toggleMode} />
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
        Header Color
      </Typography>
      <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
        {HEADER_COLORS.map((c) => (
          <Tooltip key={c.value} title={c.label} placement="top" arrow>
            <Box
              onClick={() => changeHeaderColor(c.value)}
              sx={{
                width: 28, height: 28, borderRadius: "50%", bgcolor: c.value, cursor: "pointer",
                border: headerColor === c.value ? "3px solid" : "2px solid transparent",
                borderColor: headerColor === c.value ? "text.primary" : "transparent",
                boxShadow: headerColor === c.value ? "0 0 0 2px rgba(0,0,0,0.15)" : "none",
                transition: "transform 0.15s, box-shadow 0.15s",
                "&:hover": { transform: "scale(1.15)" },
              }}
            />
          </Tooltip>
        ))}
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
        Font Size
      </Typography>
      <Box sx={{ mt: 1, display: "flex", gap: 0.75 }}>
        {FONT_SIZES.map((f) => (
          <Box
            key={f.value}
            onClick={() => changeFontSize(f.value)}
            sx={{
              flex: 1, textAlign: "center", py: 0.75, borderRadius: 1.5, cursor: "pointer",
              border: "1.5px solid", borderColor: fontSize === f.value ? "primary.main" : "divider",
              bgcolor: fontSize === f.value ? "primary.main" : "transparent",
              color: fontSize === f.value ? "#fff" : "text.secondary",
              fontWeight: 600, fontSize: "0.8rem",
              transition: "all 0.15s",
            }}
          >
            {f.label}
          </Box>
        ))}
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          Fixed Header
        </Typography>
        <Switch size="small" checked={headerFixed} onChange={toggleHeaderFixed} />
      </Box>
    </Box>
  );
};

export default AppearancePanel;
